import type { ReactNode } from 'react'
import { Link } from '@/components/layout/TransitionLink'
import { ArrowLeft } from 'lucide-react'
import { motion } from 'motion/react'
import { SplitText } from '@/components/dashboard/fx/SplitText'
import { EASE } from '@/components/dashboard/fx/motion'
import { PAGE_WIDTH } from '@/components/dashboard/fx/layout'

interface PageHeroProps {
  back?: { to: string; label: string }
  eyebrow?: string
  title: string
  accent?: string
  description?: ReactNode
  media?: ReactNode
  // A tighter header for working pages, where the content below is the point.
  compact?: boolean
  children?: ReactNode
}

// The calmer sibling of the dashboard hero: same aurora, split-word
// headline and type system, but sized for a page header rather than a
// full-viewport opener.
export function PageHero({ back, eyebrow, title, accent, description, media, compact, children }: PageHeroProps) {
  return (
    <section className="relative isolate overflow-hidden border-b border-border/50">
      <div className="aurora" aria-hidden>
        <i />
        <i />
        <i />
      </div>

      <div className={`${PAGE_WIDTH} ${compact ? 'pb-6 pt-6 sm:pb-8' : 'pb-10 pt-8 sm:pb-14'}`}>
        {back && (
          <Link
            to={back.to}
            className="group inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="size-4 transition-transform group-hover:-translate-x-1" />
            {back.label}
          </Link>
        )}

        <div className={`${compact ? 'mt-5' : 'mt-8'} flex flex-col gap-5 sm:flex-row sm:items-end sm:gap-10`}>
          {media}
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            {eyebrow && (
              <motion.span
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                className="font-mono text-xs uppercase tracking-[0.25em] text-primary"
              >
                {eyebrow}
              </motion.span>
            )}
            <h1 className={`font-display font-semibold leading-[0.95] tracking-tighter ${compact ? 'text-[clamp(2rem,4.6vw,3.75rem)]' : 'text-[clamp(2.5rem,7.5vw,6.5rem)]'}`}>
              <SplitText text={title} />
              {accent && (
                <SplitText text={accent} delay={0.25} className="font-accent font-normal italic tracking-normal text-gradient" />
              )}
            </h1>
            {description && (
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
                className="max-w-2xl text-base text-muted-foreground sm:text-lg"
              >
                {description}
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {children}
    </section>
  )
}
